import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { auth, db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
} from "firebase/firestore";

import CreatePostForm from "./views/CreatePostForm";
import AllPost from "./views/AllPosts";
import EditPostForm from "./views/EditPostForm";
import DeletePostConfirm from "./views/DeletePostConfirm";
import MessagesPromotionalContainer from "./views/MessagePromotionalContainer";
import PaymentBlock from "./PaymentBlock";
import RenewMembership from "../RenewMembership";

const ManagePromotionalPosts = ({ onClose }) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeView, setActiveView] = useState("listProfiles");

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const [postToEdit, setPostToEdit] = useState(null);
  const [postToDelete, setPostToDelete] = useState(null);

  const [showPaymentBlock, setShowPaymentBlock] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);

  // 🔹 Estado de membresía
  const [profileStatus, setProfileStatus] = useState("active"); // "active" | "expired"

  useEffect(() => {
    const fetchProfiles = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const promoProfilesRef = collection(db, "users", user.uid, "promoProfiles");
        const snapshot = await getDocs(promoProfilesRef);
        const profileList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProfiles(profileList);

        const firstProfile = profileList[0] || null;
        setSelectedProfile(firstProfile);

        // 🔹 Determinar estado de membresía
        if (firstProfile?.centralizedExpirationDate) {
          const expiration = firstProfile.centralizedExpirationDate.toDate();
          const now = new Date();
          const diffDays = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));

          setProfileStatus(expiration < now ? "expired" : "active");

          // 👇 Mostrar modal si venció o si faltan 3 días o menos
          if (expiration < now || diffDays <= 3) {
            setShowPaymentBlock(true);
          }
        }
      } catch (error) {
        console.error("Error al obtener perfiles promocionales:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  const fetchPosts = async (profileId) => {
    if (!profileId) return;

    setLoadingPosts(true);
    try {
      const postsRef = collection(db, "PromotionalPosts");
      const q = query(postsRef, where("promoProfileId", "==", profileId));
      const snapshot = await getDocs(q);
      const postList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPosts(postList);
    } catch (error) {
      console.error("Error cargando publicaciones:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  useEffect(() => {
    if (activeView === "viewPosts" && selectedProfile) {
      fetchPosts(selectedProfile.id);
    }
  }, [activeView, selectedProfile]);

  const handleEdit = (post) => {
    setPostToEdit(post);
    setActiveView("editPost");
  };

  const handleUpdatePost = async (updatedPost) => {
    try {
      const postDocRef = doc(db, "PromotionalPosts", updatedPost.id);
      await updateDoc(postDocRef, {
        title: updatedPost.title,
        description: updatedPost.description,
        images: updatedPost.images,
        videos: updatedPost.videos,
        objetivo: updatedPost.objetivo,
        categoria: updatedPost.categoria,
        fechaInicio: updatedPost.fechaInicio,
        fechaFin: updatedPost.fechaFin,
        ctaTexto: updatedPost.ctaTexto,
        ctaLink: updatedPost.ctaLink,
        descuento: updatedPost.descuento,
        destacado: updatedPost.destacado,
        visibilidad: updatedPost.visibilidad,
      });

      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Publicación actualizada correctamente", showConfirmButton: false, timer: 3000, timerProgressBar: true });
      setActiveView("viewPosts");
      fetchPosts(selectedProfile.id);
      setPostToEdit(null);
    } catch (error) {
      console.error("Error actualizando publicación:", error);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Error al actualizar publicación", showConfirmButton: false, timer: 3500 });
    }
  };

  const handleDelete = (post) => setPostToDelete(post);

  const confirmDelete = async () => {
    if (!postToDelete) return;

    try {
      const postRef = doc(db, "PromotionalPosts", postToDelete.id);
      await updateDoc(postRef, { estado: "eliminado" });

      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Publicación eliminada", showConfirmButton: false, timer: 3000, timerProgressBar: true });
      setPostToDelete(null);
      fetchPosts(selectedProfile.id);
    } catch (error) {
      console.error("Error al marcar como eliminada:", error);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Error al eliminar publicación", showConfirmButton: false, timer: 3500 });
    }
  };

  const cancelDelete = () => setPostToDelete(null);

  // 🔹 Después de renovar
  const handleRenewSuccess = (updatedProfile) => {
    setProfileStatus("active");
    setSelectedProfile(updatedProfile);
    setShowRenewModal(false);
    setShowPaymentBlock(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 dark:bg-gray-900 dark:bg-opacity-80">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl flex flex-col md:flex-row overflow-hidden max-h-[90vh]">
        
        <aside className="md:w-1/3 bg-gray-50 dark:bg-gray-900 p-6 flex flex-col space-y-6 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 overflow-y-auto" style={{ minHeight: "400px" }}>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Perfiles Promocionales</h2>

          {loading ? (
            <p className="text-gray-600 dark:text-gray-400">Cargando perfiles...</p>
          ) : profiles.length === 0 ? (
            <p className="text-red-500 font-medium">No tienes perfiles promocionales. Crea uno primero.</p>
          ) : (
            <>
              <label htmlFor="profile-select" className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Seleccionar perfil:
              </label>
              <select
                id="profile-select"
                value={selectedProfile?.id || ""}
                onChange={(e) => {
                  const profile = profiles.find((p) => p.id === e.target.value);
                  setSelectedProfile(profile);

                  if (profile?.centralizedExpirationDate) {
                    const expiration = profile.centralizedExpirationDate.toDate();
                    const now = new Date();
                    const diffDays = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));

                    setProfileStatus(expiration < now ? "expired" : "active");

                    // 👇 Modal si venció o faltan 3 días o menos
                    if (expiration < now || diffDays <= 3) {
                      setShowPaymentBlock(true);
                    } else {
                      setShowPaymentBlock(false);
                    }
                  }

                  setActiveView("listProfiles");
                }}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
              >
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>{profile.name}</option>
                ))}
              </select>

              <div className="flex flex-col space-y-3 mt-6">
                <button
                  className={`w-full py-2 rounded-md font-semibold transition focus:outline-none focus:ring-4
                    ${profileStatus !== "active" ? "bg-gray-400 text-gray-200 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}
                  onClick={() => profileStatus === "active" && setActiveView("viewPosts")}
                  disabled={profileStatus !== "active"}
                >
                  Ver todas las publicaciones
                </button>

                <button
                  className={`w-full py-2 rounded-md font-semibold transition focus:outline-none focus:ring-4
                    ${profileStatus !== "active" ? "bg-gray-400 text-gray-200 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 text-white"}`}
                  onClick={() => profileStatus === "active" && setActiveView("createPost")}
                  disabled={profileStatus !== "active"}
                >
                  Hacer publicación
                </button>

                <button
                  className={`w-full py-2 rounded-md font-semibold transition focus:outline-none focus:ring-4
                    ${profileStatus !== "active" ? "bg-gray-400 text-gray-200 cursor-not-allowed" : "bg-yellow-600 hover:bg-yellow-700 text-white"}`}
                  onClick={() => profileStatus === "active" && setActiveView("messagesPromotional")}
                  disabled={profileStatus !== "active"}
                >
                  Mensajes
                </button>
              </div>
            </>
          )}

          <button onClick={onClose} className="mt-auto py-2 w-full rounded-md bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 font-semibold transition focus:outline-none focus:ring-4 focus:ring-gray-400">
            Cerrar
          </button>
        </aside>

        <main className="md:w-2/3 p-6 overflow-y-auto max-h-[90vh] bg-white dark:bg-gray-800">
          {activeView === "createPost" && selectedProfile && profileStatus === "active" && (
            <CreatePostForm promoProfileId={selectedProfile.id} userId={auth.currentUser.uid} onCreated={() => setActiveView("viewPosts")} />
          )}

          {activeView === "viewPosts" && selectedProfile && profileStatus === "active" && (
            <>
              {loadingPosts ? (
                <p className="text-gray-600 dark:text-gray-400">Cargando publicaciones...</p>
              ) : posts.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No hay publicaciones para este perfil.</p>
              ) : (
                <AllPost posts={posts} onEdit={handleEdit} onDelete={handleDelete} />
              )}
            </>
          )}

          {activeView === "editPost" && postToEdit && profileStatus === "active" && (
            <EditPostForm post={postToEdit} onCancel={() => setActiveView("viewPosts")} onSave={handleUpdatePost} />
          )}

          {activeView === "messagesPromotional" && selectedProfile && profileStatus === "active" && (
            <MessagesPromotionalContainer promoProfileId={selectedProfile.id} onOpenAdmin={() => setActiveView("viewPosts")} />
          )}
        </main>

        {postToDelete && <DeletePostConfirm post={postToDelete} onConfirm={confirmDelete} onCancel={cancelDelete} />}

        {selectedProfile && showPaymentBlock && (
          <PaymentBlock
            profile={selectedProfile}
            onClose={() => setShowPaymentBlock(false)}
            onRenewMembership={() => setShowRenewModal(true)}
          />
        )}

        {showRenewModal && (
          <RenewMembership
            isOpen={showRenewModal}
            onClose={() => setShowRenewModal(false)}
            onSuccess={handleRenewSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default ManagePromotionalPosts;
